/* WOLF BROS – virtual fit preview.
   Draws the customer (from height + weight) wearing this product in every size,
   and recommends a size for each fit style (fitted / regular / relaxed / oversized).
   Remembers the customer (height + weight) and shows "Your size" on every product + in the size chart.
   If their size is sold out / not made, offers the closest smaller (a bit tight) and bigger (a bit loose) size. */
(function () {
  'use strict';

  var COLORS = {
    black: '#1b1b1d', white: '#f2f2ef', 'off white': '#ece8dc', cream: '#efe6cf', ivory: '#f1ead8', beige: '#d8c3a0', sand: '#d6c19a', camel: '#b8895a',
    khaki: '#b5a77c', oatmeal: '#d9ccb1', brown: '#6b4a33', 'dark brown': '#4a3324', chocolate: '#4b2e22', mocha: '#7a5a46',
    grey: '#8d8f93', gray: '#8d8f93', 'light grey': '#c4c6c9', 'light gray': '#c4c6c9', 'heather grey': '#a9abaf', 'dark grey': '#4c4e52', 'dark gray': '#4c4e52', charcoal: '#3a3c40', silver: '#b8bbbf',
    navy: '#1f2a44', 'dark navy': '#18213a', 'light navy': '#36466b', blue: '#2f5fa8', 'light blue': '#8fb4dc', 'sky blue': '#8ec5ea', 'baby blue': '#a9cbe8', indigo: '#2c3566', denim: '#3d5a80', teal: '#1f6f73', turquoise: '#36b5b0',
    green: '#2f6b3e', olive: '#5d6236', 'dark green': '#22432c', sage: '#9caf88', mint: '#a8dcc0', 'army green': '#4b5320', 'forest green': '#244a2f',
    red: '#b3262e', burgundy: '#6d1f2b', maroon: '#5c1a22', wine: '#5e1d2c', coral: '#ee7a64', orange: '#e0782f', rust: '#a4492a', pink: '#e7a2b4', 'light pink': '#f2c6d1', rose: '#c9707f',
    yellow: '#e9c93f', mustard: '#c99a2e', lime: '#c8e34a', purple: '#5b3f8c', lavender: '#b7a6d9', lilac: '#c3a9d6'
  };
  var colorOf = function (name) {
    if (!name) return '#7d8087';
    var n = String(name).toLowerCase().trim();
    if (COLORS[n]) return COLORS[n];
    var best = null;
    Object.keys(COLORS).forEach(function (k) { if (n.indexOf(k) > -1 && (!best || k.length > best.length)) best = k; });
    return best ? COLORS[best] : '#7d8087';
  };
  var hexToRgb = function (h) { h = h.replace('#', ''); return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)]; };
  var shade = function (h, f) { var c = hexToRgb(h).map(function (v) { return Math.max(0, Math.min(255, Math.round(f < 0 ? v * (1 + f) : v + (255 - v) * f))); }); return 'rgb(' + c.join(',') + ')'; };
  var lum = function (h) { var c = hexToRgb(h); return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255; };
  var esc = function (s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };

  /* ---------- garment type from the product type ---------- */
  var garmentType = function (ptype, kind) {
    var t = String(ptype || '').toLowerCase();
    if (kind === 'bottoms' || kind === 'jeans') {
      if (/short|swim|boxer/.test(t)) return 'shorts';
      if (/jogger|sweatpant/.test(t)) return 'jogger';
      return 'pants';
    }
    if (/hood/.test(t)) return 'hoodie';
    if (/vest|undershirt|tank/.test(t)) return 'vest';
    if (/sweat|pullover|turtle|shirt$|^shirts|jacket|set/.test(t) && !/t-shirt|tshirt|polo/.test(t)) return 'long';
    if (/polo/.test(t)) return 'polo';
    return 'tee';
  };

  /* ---------- body estimate ---------- */
  var bodyFrom = function (h, w) {
    return {
      chest: 58 + 0.5 * w + 0.1 * (h - 175),
      waist: 0.6 * w + 0.15 * h + 8,
      bmi: w / Math.pow(h / 100, 2)
    };
  };

  /* ---------- SVG figure ---------- */
  var r1 = function (n) { return Math.round(n * 10) / 10; };
  var pts = function (arr) { return arr.map(function (p) { return r1(p[0]) + ',' + r1(p[1]); }).join(' '); };
  var mirror = function (arr) { return arr.slice().reverse().map(function (p) { return [100 - p[0], p[1]]; }); };

  function drawFigure(o) {
    /* o: h, w, gtype, ratio (garment/body), fitKey, color, lengthAdd */
    var h = Math.max(140, Math.min(205, o.h));
    var bmi = o.w / Math.pow(h / 100, 2);
    var k = Math.max(0.78, Math.min(1.75, Math.pow(bmi / 22, 1.25)));
    var sc = h / 175;
    var top = 226 - h;
    var Y = function (f) { return top + f * h; };
    var headR = 0.062 * h, neckY = Y(0.14), shY = Y(0.185), chestY = Y(0.26), waistY = Y(0.40), hipY = Y(0.49), crotchY = Y(0.53), kneeY = Y(0.74), ankleY = Y(0.965);
    var hs = 20 * sc * (0.9 + 0.1 * k), hc = 16 * sc * Math.pow(k, 0.85), hw = 13.2 * sc * k, hh = 15.5 * sc * (0.88 + 0.14 * k);
    var thigh = 7.6 * sc * Math.pow(k, 0.9), ankle = 3.4 * sc, at = 4.8 * sc * Math.pow(k, 0.7);
    var skin = '#7a7b82', skinD = '#696a71';

    var s = '';
    /* mannequin */
    s += '<circle cx="50" cy="' + r1(top + headR) + '" r="' + r1(headR) + '" fill="' + skin + '"/>';
    s += '<rect x="' + r1(50 - 4 * sc) + '" y="' + r1(top + headR * 1.7) + '" width="' + r1(8 * sc) + '" height="' + r1(shY - top - headR * 1.6) + '" fill="' + skinD + '"/>';
    var arm = function (side) {
      var sx = 50 + side * (hs - at * 0.6), ex = 50 + side * (hs + 1.5 * sc + (k - 1) * 4), hx = 50 + side * (hs + 2.5 * sc + (k - 1) * 5);
      return '<path d="M' + r1(sx) + ' ' + r1(shY + at * 0.8) + ' L' + r1(ex) + ' ' + r1(Y(0.37)) + ' L' + r1(hx) + ' ' + r1(Y(0.5)) + '" stroke="' + skinD + '" stroke-width="' + r1(at * 2) + '" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
    };
    s += arm(-1) + arm(1);
    var torso = [[50 - 4 * sc, neckY], [50 - hs, shY + 1.5], [50 - hc, chestY], [50 - hw, waistY], [50 - hh, hipY], [50 - hh + 1, crotchY]];
    s += '<polygon points="' + pts(torso.concat(mirror(torso))) + '" fill="' + skin + '" stroke="' + skin + '" stroke-width="3" stroke-linejoin="round"/>';
    var leg = [[50 - hh, hipY], [50 - 0.8, crotchY], [50 - 1.6, kneeY], [50 - 2.2, ankleY], [50 - 2.2 - 2 * ankle, ankleY], [50 - 2.6 - 2 * thigh * 0.72, kneeY], [50 - hh - 0.5, crotchY - 2]];
    s += '<polygon points="' + pts(leg) + '" fill="' + skinD + '" stroke="' + skinD + '" stroke-width="2" stroke-linejoin="round"/>';
    s += '<polygon points="' + pts(leg.map(function (p) { return [100 - p[0], p[1]]; })) + '" fill="' + skinD + '" stroke="' + skinD + '" stroke-width="2" stroke-linejoin="round"/>';
    s += '<ellipse cx="' + r1(50 - 3.5 - ankle) + '" cy="' + r1(ankleY + 2) + '" rx="' + r1(ankle * 1.7) + '" ry="2.2" fill="#55565c"/>';
    s += '<ellipse cx="' + r1(50 + 3.5 + ankle) + '" cy="' + r1(ankleY + 2) + '" rx="' + r1(ankle * 1.7) + '" ry="2.2" fill="#55565c"/>';

    /* garment */
    var col = o.color, dark = shade(col, -0.28), light = shade(col, 0.18);
    var outline = lum(col) < 0.25 ? 'rgba(255,255,255,.38)' : 'rgba(0,0,0,.35)';
    var g = '';
    /* exaggerate the difference a little so the eye can see it on a small figure */
    var r = Math.max(0.94, Math.min(1.95, 1 + (o.ratio - 1) * 1.7));
    var room = Math.max(1, o.ratio);
    var folds = '';
    var tension = '';

    if (/tee|polo|long|hoodie|vest/.test(o.gtype)) {
      var drop = Math.max(0, (r - 1.16)) * 26 * sc;          /* dropped shoulders when it is big */
      var gsX = hs * Math.min(1.04 + Math.max(0, r - 1.16) * 0.9, 1.3);
      var gw = hc * Math.max(r, 1.0) + 0.6;
      var gWaist = Math.max(gw * 0.97, hw * Math.max(r, 1) + 0.8);
      var hemY = hipY + 2 + (r - 1) * 20 * sc + (o.lengthAdd || 0) * sc;
      var hemW = Math.max(gWaist, (o.gtype === 'hoodie' || o.gtype === 'long') && r < 1.2 ? hh * 0.98 : gWaist);
      var neckW = 6.2 * sc;
      var body = [[50 - neckW, neckY + 0.5], [50 - gsX, shY + drop * 0.6], [50 - gw, chestY + drop], [50 - gWaist, waistY], [50 - hemW, hemY]];
      var bodyR = mirror(body);
      var neckDip = o.gtype === 'polo' ? 3.2 * sc : 5 * sc;
      var dBody = 'M' + pts(body).replace(/ /g, ' L') + ' L' + pts(bodyR).replace(/ /g, ' L') + ' Q50 ' + r1(neckY + neckDip) + ' ' + r1(50 - neckW) + ' ' + r1(neckY + 0.5) + ' Z';

      /* sleeves */
      var sleeve = function (side) {
        if (o.gtype === 'vest') return '';
        var sx = 50 + side * gsX, sy = shY + drop * 0.6;
        var ax = 50 + side * gw, ay = chestY + drop + 1;
        var long = o.gtype === 'long' || o.gtype === 'hoodie';
        var endY = long ? Y(0.495) : Y(0.30) + (r - 1) * 10 * sc + drop * 0.5;
        var reach = long ? (hs + 2.5 * sc + (k - 1) * 5) : (hs + 1.2 * sc + (k - 1) * 3);
        var sw = at * (long ? 1.25 : 1.55) * Math.max(1, Math.min(r, 1.5));
        var ox = 50 + side * (reach + sw * 0.55), ix = 50 + side * (reach - sw * 0.75);
        if (long) {
          var elY = Y(0.37), eox = 50 + side * (hs + 1.5 * sc + (k - 1) * 4 + sw * 0.6), eix = 50 + side * (hs + 1.5 * sc + (k - 1) * 4 - sw * 0.7);
          var cuffY = endY + 1.2;
          return '<path d="M' + r1(sx) + ' ' + r1(sy) + ' L' + r1(eox) + ' ' + r1(elY) + ' L' + r1(ox) + ' ' + r1(endY) + ' L' + r1(ix) + ' ' + r1(cuffY) + ' L' + r1(eix) + ' ' + r1(elY + 1) + ' L' + r1(ax) + ' ' + r1(ay) + ' Z" fill="' + col + '" stroke="' + outline + '" stroke-width=".5" stroke-linejoin="round"/>' +
            '<path d="M' + r1(ox - side * 0.3) + ' ' + r1(endY - 2) + ' L' + r1(ix + side * 0.3) + ' ' + r1(cuffY - 2) + '" stroke="' + dark + '" stroke-width="1.6" stroke-linecap="round"/>';
        }
        return '<path d="M' + r1(sx) + ' ' + r1(sy) + ' L' + r1(ox) + ' ' + r1(endY) + ' L' + r1(ix) + ' ' + r1(endY + 2) + ' L' + r1(ax) + ' ' + r1(ay) + ' Z" fill="' + col + '" stroke="' + outline + '" stroke-width=".5" stroke-linejoin="round"/>';
      };
      g += sleeve(-1) + sleeve(1);

      if (o.gtype === 'hoodie') {
        /* hood resting behind the neck */
        g += '<path d="M' + r1(50 - neckW - 3 * sc) + ' ' + r1(neckY + 1) + ' Q' + r1(50 - headR * 1.25) + ' ' + r1(neckY - headR * 0.9) + ' 50 ' + r1(neckY - headR * 0.75) + ' Q' + r1(50 + headR * 1.25) + ' ' + r1(neckY - headR * 0.9) + ' ' + r1(50 + neckW + 3 * sc) + ' ' + r1(neckY + 1) + ' Z" fill="' + dark + '"/>';
      }
      g += '<path d="' + dBody + '" fill="' + col + '" stroke="' + outline + '" stroke-width=".55" stroke-linejoin="round"/>';
      /* light from the left */
      g += '<path d="M' + r1(50 - gw + 1.5) + ' ' + r1(chestY + drop + 2) + ' L' + r1(50 - gWaist + 1.5) + ' ' + r1(waistY) + ' L' + r1(50 - hemW + 1.5) + ' ' + r1(hemY - 1) + ' L' + r1(50 - hemW * 0.55) + ' ' + r1(hemY - 1) + ' L' + r1(50 - gw * 0.5) + ' ' + r1(chestY + drop + 2) + ' Z" fill="' + light + '" opacity=".22"/>';
      /* collar / neck rib */
      if (o.gtype === 'polo') {
        g += '<path d="M' + r1(50 - neckW) + ' ' + r1(neckY + 0.5) + ' L' + r1(50 - 1) + ' ' + r1(neckY + 5 * sc) + ' L' + r1(50 - neckW - 1.5) + ' ' + r1(neckY + 5.5 * sc) + ' Z" fill="' + dark + '"/>' +
             '<path d="M' + r1(50 + neckW) + ' ' + r1(neckY + 0.5) + ' L' + r1(50 + 1) + ' ' + r1(neckY + 5 * sc) + ' L' + r1(50 + neckW + 1.5) + ' ' + r1(neckY + 5.5 * sc) + ' Z" fill="' + dark + '"/>' +
             '<line x1="50" y1="' + r1(neckY + 3 * sc) + '" x2="50" y2="' + r1(neckY + 11 * sc) + '" stroke="' + dark + '" stroke-width=".8"/>';
      } else {
        g += '<path d="M' + r1(50 - neckW) + ' ' + r1(neckY + 0.5) + ' Q50 ' + r1(neckY + neckDip) + ' ' + r1(50 + neckW) + ' ' + r1(neckY + 0.5) + '" stroke="' + dark + '" stroke-width="1.4" fill="none"/>';
      }
      if (o.gtype === 'hoodie') {
        var pw = gWaist * 0.75, py = waistY + 1;
        g += '<path d="M' + r1(50 - pw) + ' ' + r1(hemY - 3) + ' L' + r1(50 - pw * 0.8) + ' ' + r1(py) + ' L' + r1(50 + pw * 0.8) + ' ' + r1(py) + ' L' + r1(50 + pw) + ' ' + r1(hemY - 3) + '" stroke="' + dark + '" stroke-width=".8" fill="none"/>';
        g += '<line x1="' + r1(48.2) + '" y1="' + r1(neckY + 3) + '" x2="' + r1(47.6) + '" y2="' + r1(neckY + 14 * sc) + '" stroke="' + light + '" stroke-width=".9" stroke-linecap="round"/>';
        g += '<line x1="' + r1(51.8) + '" y1="' + r1(neckY + 3) + '" x2="' + r1(52.4) + '" y2="' + r1(neckY + 14 * sc) + '" stroke="' + light + '" stroke-width=".9" stroke-linecap="round"/>';
      }
      if (o.gtype === 'long' || o.gtype === 'hoodie') {
        g += '<path d="M' + r1(50 - hemW) + ' ' + r1(hemY - 2.2) + ' L' + r1(50 + hemW) + ' ' + r1(hemY - 2.2) + '" stroke="' + dark + '" stroke-width="1.4"/>';
      }
      /* fabric folds when there is room, tension when tight */
      if (room > 1.12) {
        var n = room > 1.3 ? 3 : 2;
        for (var i = 0; i < n; i++) {
          var fx = 50 - gWaist + 2.5 + i * 2.2;
          folds += '<path d="M' + r1(fx) + ' ' + r1(chestY + drop + 6 + i * 3) + ' Q' + r1(fx + 1.5) + ' ' + r1((waistY + hemY) / 2) + ' ' + r1(fx + 0.6) + ' ' + r1(hemY - 2) + '" stroke="' + dark + '" stroke-width=".6" fill="none" opacity=".55"/>';
          folds += '<path d="M' + r1(100 - fx) + ' ' + r1(chestY + drop + 6 + i * 3) + ' Q' + r1(100 - fx - 1.5) + ' ' + r1((waistY + hemY) / 2) + ' ' + r1(100 - fx - 0.6) + ' ' + r1(hemY - 2) + '" stroke="' + dark + '" stroke-width=".6" fill="none" opacity=".55"/>';
        }
      }
      if (o.ratio < 1.035) {
        for (var j = 0; j < 3; j++) {
          var ty = chestY + 2 + j * 3.2;
          tension += '<path d="M' + r1(50 - hc + 2) + ' ' + r1(ty) + ' Q50 ' + r1(ty - 2.4) + ' ' + r1(50 + hc - 2) + ' ' + r1(ty) + '" stroke="' + dark + '" stroke-width=".55" fill="none" opacity=".7"/>';
        }
      }
    } else {
      /* bottoms */
      var legF = 1 + Math.max(0, r - 1) * 2.2;
      var short = o.gtype === 'shorts';
      var endY = short ? Y(0.665) : (o.gtype === 'jogger' ? ankleY - 1 : ankleY + 0.5);
      var wbY = waistY + 1.5;
      var wbW = hw * Math.max(r, 0.97) + 1;
      var hipW = hh * Math.max(1, 0.96 + (r - 1) * 1.2) + 0.8;
      var kneeOut = 2.6 + 2 * thigh * 0.72 * legF + 0.6, kneeIn = 1.2;
      var endOut = short ? (2.4 + 2 * thigh * 0.85 * legF) : (o.gtype === 'jogger' ? 2.2 + 2 * ankle * 1.05 : 2.2 + 2 * ankle * (1.25 + (legF - 1) * 2.4));
      var endIn = short ? 0.9 : 1.6;
      var shortKnee = short ? [[50 - endOut, endY], [50 - endIn, endY]] : [[50 - kneeOut, kneeY], [50 - endOut, endY], [50 - endIn, endY], [50 - kneeIn, kneeY]];
      var left = [[50, wbY], [50 - wbW, wbY], [50 - hipW, hipY]].concat(shortKnee).concat([[50, crotchY + 1.5]]);
      var right = left.map(function (p) { return [100 - p[0], p[1]]; }).reverse();
      g += '<polygon points="' + pts(left.concat(right)) + '" fill="' + col + '" stroke="' + outline + '" stroke-width=".55" stroke-linejoin="round"/>';
      g += '<rect x="' + r1(50 - wbW) + '" y="' + r1(wbY) + '" width="' + r1(wbW * 2) + '" height="' + r1(3 * sc) + '" fill="' + dark + '"/>';
      if (o.gtype === 'jogger') {
        g += '<rect x="' + r1(50 - endOut) + '" y="' + r1(endY - 2.6) + '" width="' + r1(endOut - endIn) + '" height="2.6" fill="' + dark + '"/>' +
             '<rect x="' + r1(50 + endIn) + '" y="' + r1(endY - 2.6) + '" width="' + r1(endOut - endIn) + '" height="2.6" fill="' + dark + '"/>';
        g += '<line x1="49" y1="' + r1(wbY + 2) + '" x2="48.3" y2="' + r1(wbY + 10 * sc) + '" stroke="' + light + '" stroke-width=".8"/><line x1="51" y1="' + r1(wbY + 2) + '" x2="51.7" y2="' + r1(wbY + 10 * sc) + '" stroke="' + light + '" stroke-width=".8"/>';
      }
      if (/jeans/.test(o.kind || '')) {
        g += '<line x1="50" y1="' + r1(wbY + 3) + '" x2="50" y2="' + r1(crotchY - 2) + '" stroke="' + dark + '" stroke-width=".7"/>';
      }
      /* light on the left leg */
      g += '<polygon points="' + pts([[50 - wbW + 1, wbY + 3], [50 - hipW + 1.2, hipY], [50 - (short ? endOut : kneeOut) + 1.2, short ? endY - 1 : kneeY], [50 - (short ? endOut : kneeOut) * 0.55, short ? endY - 1 : kneeY], [50 - wbW * 0.45, wbY + 3]]) + '" fill="' + light + '" opacity=".2"/>';
      if (o.ratio > 1.06) {
        var gathers = '';
        for (var q = 0; q < 4; q++) { var gx = 50 - wbW + 2 + q * (wbW * 2 - 4) / 3; gathers += '<line x1="' + r1(gx) + '" y1="' + r1(wbY + 0.4) + '" x2="' + r1(gx + 0.3) + '" y2="' + r1(wbY + 4.5) + '" stroke="' + dark + '" stroke-width=".6" opacity=".8"/>'; }
        folds += gathers;
      }
      if (legF > 1.15 && !short) {
        folds += '<path d="M' + r1(50 - kneeOut + 2) + ' ' + r1(kneeY - 8) + ' q1.2 7 0.3 14" stroke="' + dark + '" stroke-width=".6" fill="none" opacity=".6"/>';
        folds += '<path d="M' + r1(50 + kneeOut - 2) + ' ' + r1(kneeY - 8) + ' q-1.2 7 -0.3 14" stroke="' + dark + '" stroke-width=".6" fill="none" opacity=".6"/>';
      }
      if (o.ratio < 0.985) {
        tension += '<path d="M' + r1(50 - hipW + 2) + ' ' + r1(hipY - 2) + ' Q50 ' + r1(hipY - 5) + ' ' + r1(50 + hipW - 2) + ' ' + r1(hipY - 2) + '" stroke="' + dark + '" stroke-width=".55" fill="none" opacity=".75"/>';
        tension += '<path d="M' + r1(50 - hipW + 3) + ' ' + r1(hipY + 2) + ' Q50 ' + r1(hipY - 1) + ' ' + r1(50 + hipW - 3) + ' ' + r1(hipY + 2) + '" stroke="' + dark + '" stroke-width=".55" fill="none" opacity=".75"/>';
      }
    }

    var vbTop = r1(top - 3);
    return '<svg viewBox="6 ' + vbTop + ' 88 ' + r1(233 - vbTop) + '" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="' + (o.label || '') + '">' +
      '<ellipse cx="50" cy="228.5" rx="24" ry="2.4" fill="rgba(0,0,0,.25)"/>' + s + g + folds + tension + '</svg>';
  }

  /* ---------- fit maths ---------- */
  var rangeOf = function (s) { var m = String(s).match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/); if (m) return [+m[1], +m[2]]; var n = String(s).match(/\d+(?:\.\d+)?/); return n ? [+n[0], +n[0]] : null; };

  var FITS_TOP = [
    { key: 'fitted', name: 'Fitted', target: 6, desc: 'Close to the body, shows your shape' },
    { key: 'regular', name: 'Regular', target: 13, desc: 'Classic everyday fit' },
    { key: 'relaxed', name: 'Relaxed', target: 21, desc: 'A bit loose and comfy' },
    { key: 'oversized', name: 'Oversized', target: 31, desc: 'Big, street-style look' }
  ];
  var FITS_BOTTOM = [
    { key: 'fitted', name: 'Snug', target: 0.5, desc: 'Sits tight on the waist' },
    { key: 'regular', name: 'Regular', target: 3, desc: 'Comfortable everyday fit' },
    { key: 'relaxed', name: 'Relaxed', target: 6.5, desc: 'Roomy, wear it low or with a belt' }
  ];
  var fitName = function (kind, ease) {
    if (kind === 'tops') {
      if (ease < 2) return { key: 'tight', name: 'Too tight' };
      if (ease < 9.5) return { key: 'fitted', name: 'Fitted' };
      if (ease < 17) return { key: 'regular', name: 'Regular' };
      if (ease < 26) return { key: 'relaxed', name: 'Relaxed' };
      return { key: 'oversized', name: 'Oversized' };
    }
    if (ease < -1.5) return { key: 'tight', name: 'Too tight' };
    if (ease < 1.8) return { key: 'fitted', name: 'Snug' };
    if (ease < 5) return { key: 'regular', name: 'Regular' };
    if (ease < 9) return { key: 'relaxed', name: 'Relaxed' };
    return { key: 'big', name: 'Too big' };
  };

  /* ctx.all = keep chart sizes this product is not made in (to know the customer's real size) */
  function build(ctx) {
    var kind = ctx.kind, tops = kind === 'tops';
    var head = ctx.head;
    var colIdx = function (word) { for (var i = 0; i < head.length; i++) if (head[i].indexOf(word) > -1) return i; return -1; };
    var ci = tops ? colIdx('chest') : colIdx('waist');
    var hi = colIdx('height');
    var body = bodyFrom(ctx.h, ctx.w);
    var bodyMeasure = tops ? body.chest : body.waist;
    var list = ctx.sizes || [];
    var out = [];
    ctx.rows.forEach(function (row, i) {
      var rg = ci > -1 ? rangeOf(row.cells[ci]) : null;
      var garment;
      if (rg) {
        if (tops) garment = rg[1] + 6 + (ctx.oversized ? 14 : 0);
        else if (kind === 'jeans') garment = rg[0] + 1.5;
        else garment = (rg[0] + rg[1]) / 2 + 2.5;
      } else {
        garment = bodyMeasure + (i - 2) * 5;
      }
      var p = list.length ? ctx.match(list, row.size) : null;
      if (list.length && !p && !ctx.all) return; /* this product is not made in that size */
      var ease = garment - bodyMeasure;
      var fit = fitName(kind, ease);
      /* length: a size made for people much shorter than you will be too short */
      var hr = hi > -1 ? rangeOf(row.cells[hi]) : null;
      var short = hr && ctx.h > hr[1] + (tops ? 9 : 7);
      if (short && fit.key !== 'tight') fit = { key: 'short', name: tops ? 'Too short' : 'Too short in the leg' };
      out.push({ i: i, row: row, size: p ? p.value : row.size, pick: p, made: !list.length || !!p, soldOut: p ? p.soldOut : false, garment: garment, ease: ease, ratio: garment / bodyMeasure, fit: fit });
    });
    return { body: body, sizes: out, measure: bodyMeasure };
  }
  var okFit = function (s) { return s.fit.key !== 'tight' && s.fit.key !== 'big' && s.fit.key !== 'short'; };
  var targetOf = function (kind, fit) { return kind === 'tops' ? (fit < 0 ? 6 : fit > 0 ? 21 : 13) : (fit < 0 ? 0.5 : fit > 0 ? 6.5 : 3); };
  var copy = function (o, extra) { var r = {}, k; for (k in o) r[k] = o[k]; for (k in extra) r[k] = extra[k]; return r; };

  /* the customer's real size from the chart – no matter if it is in stock */
  function idealOf(ctx, target) {
    var all = build(copy(ctx, { all: true })).sizes, best = null;
    all.forEach(function (s) { if (!okFit(s)) return; var d = Math.abs(s.ease - target); if (!best || d < best.d) best = { s: s, d: d }; });
    if (!best && all.length) best = { s: all.slice().sort(function (a, b) { return Math.abs(a.ease - target) - Math.abs(b.ease - target); })[0] };
    return best ? { s: best.s, all: all } : null;
  }
  /* closest buyable sizes around the ideal one */
  function nearby(all, ideal) {
    var ok = function (s) { return s.made && s.pick && !s.soldOut; };
    var out = { smaller: null, larger: null };
    for (var a = ideal.i - 1; a >= ideal.i - 2; a--) { var sa = all.filter(function (x) { return x.i === a; })[0]; if (sa && ok(sa)) { out.smaller = sa; break; } }
    for (var b = ideal.i + 1; b <= ideal.i + 2; b++) { var sb = all.filter(function (x) { return x.i === b; })[0]; if (sb && ok(sb)) { out.larger = sb; break; } }
    var feel = function (s, dir) {
      var steps = Math.abs(s.i - ideal.i);
      if (dir < 0) return (s.fit.key === 'tight' || steps > 1) ? 'tight' : 'a bit tight';
      return (s.fit.key === 'big' || s.fit.key === 'oversized' || steps > 1) ? 'loose' : 'a bit loose';
    };
    if (out.smaller) out.smaller.feel = feel(out.smaller, -1);
    if (out.larger) out.larger.feel = feel(out.larger, 1);
    return out;
  }
  var whyOut = function (s) { return !s.made ? 'isn\'t made in this item' : 'is sold out in this colour'; };

  /* ---------- UI ---------- */
  var css = '' +
    '.wb-sg .sg-box{max-width:820px}' +
    '.wbf-you{display:flex;align-items:center;gap:8px;font-size:12px;opacity:.75;margin:2px 0 12px}' +
    '.wbf-note{margin:0 0 12px;padding:10px 12px;border-radius:12px;border:1px dashed #ffb84d;font-size:13px;line-height:1.5}' +
    '.wbf-recs{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,210px));justify-content:center;gap:10px}' +
    '.wbf-card{position:relative;display:flex;flex-direction:column;align-items:center;text-align:center;padding:12px 10px 12px;border-radius:14px;border:1px solid rgba(var(--color-foreground),.12);background:rgba(var(--color-foreground),.04);cursor:pointer;transition:border-color .2s,transform .2s,background .2s;animation:wbfIn .4s cubic-bezier(.2,.8,.2,1) both}' +
    '.wbf-card:hover{transform:translateY(-2px)}' +
    '.wbf-card.is-pref{border-color:var(--wb-acc,#E3FF4A);background:rgba(227,255,74,.08)}' +
    '.wbf-card .wbf-tag{position:absolute;top:8px;left:8px;font-size:10px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;padding:3px 7px;border-radius:999px;background:var(--wb-acc,#E3FF4A);color:#111}' +
    '.wbf-card svg{width:100%;max-width:120px;height:auto;display:block}' +
    '.wbf-fit{font-size:11px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;opacity:.75;margin-top:6px}' +
    '.wbf-size{font-size:26px;font-weight:800;line-height:1.1;margin:2px 0}' +
    '.wbf-desc{font-size:12px;opacity:.7;line-height:1.35;min-height:32px}' +
    '.wbf-room{font-size:11px;opacity:.6;margin-top:4px}' +
    '.wbf-btn{margin-top:10px;width:100%;height:38px;border-radius:999px;border:0;background:rgb(var(--color-foreground));color:rgb(var(--color-background));font-weight:800;font-size:12px;letter-spacing:.06em;text-transform:uppercase;cursor:pointer}' +
    '.wbf-card.is-pref .wbf-btn{background:var(--wb-acc,#E3FF4A);color:#111}' +
    '.wbf-btn[disabled]{opacity:.4;cursor:not-allowed}' +
    '.wbf-btn.is-on{background:var(--wb-acc,#E3FF4A);color:#111}' +
    '.wbf-picked{display:flex;align-items:center;gap:10px;margin:0 0 12px;padding:10px 12px;border-radius:12px;background:var(--wb-acc,#E3FF4A);color:#111;font-size:13px;line-height:1.4;animation:wbfIn .35s cubic-bezier(.2,.8,.2,1)}' +
    '.wbf-picked>span:first-child{flex:0 0 22px;height:22px;border-radius:50%;display:grid;place-items:center;background:#111;color:var(--wb-acc,#E3FF4A);font-weight:900}' +
    '.wbf-picked b{font-size:15px}' +
    '.wbf-done{margin-left:auto;flex:0 0 auto;height:34px;padding:0 16px;border-radius:999px;border:0;background:#111;color:#fff;font:inherit;font-weight:800;font-size:12px;letter-spacing:.06em;text-transform:uppercase;cursor:pointer}' +
    '.wbf-try{margin-top:18px;padding:14px;border-radius:14px;background:rgba(var(--color-foreground),.05)}' +
    '.wbf-try h4{margin:0 0 4px;font-size:15px}' +
    '.wbf-try p{margin:0 0 10px;font-size:12px;opacity:.7}' +
    '.wbf-stage{display:grid;grid-template-columns:150px 1fr;gap:14px;align-items:center}' +
    '.wbf-stage svg{width:100%;height:auto;display:block}' +
    '.wbf-chips{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px}' +
    '.wbf-chip{min-width:44px;height:36px;padding:0 10px;border-radius:999px;border:1px solid rgba(var(--color-foreground),.25);background:none;color:inherit;font-weight:700;font-size:13px;cursor:pointer}' +
    '.wbf-chip[aria-pressed="true"]{background:var(--wb-acc,#E3FF4A);border-color:var(--wb-acc,#E3FF4A);color:#111}' +
    '.wbf-chip.is-out{opacity:.45;text-decoration:line-through}' +
    '.wbf-meter{position:relative;height:8px;border-radius:999px;background:linear-gradient(90deg,#ff6b6b 0,#ffb84d 14%,#E3FF4A 30%,#7ee08a 55%,#6cc3ff 80%,#a78bfa 100%);margin:18px 0 6px}' +
    '.wbf-meter i{position:absolute;top:50%;width:16px;height:16px;margin:-8px 0 0 -8px;border-radius:50%;background:#fff;border:3px solid #111;transition:left .35s cubic-bezier(.2,.8,.2,1)}' +
    '.wbf-scale{display:flex;justify-content:space-between;font-size:10px;letter-spacing:.06em;text-transform:uppercase;opacity:.6}' +
    '.wbf-now{font-size:14px;margin-top:10px;line-height:1.45}' +
    '.wbf-now b{font-size:18px}' +
    '.wbf-disc{font-size:11px;opacity:.55;margin-top:12px;line-height:1.4}' +
    '@keyframes wbfIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}' +
    '@media (max-width:749px){.wbf-recs{grid-template-columns:repeat(2,1fr)}.wbf-stage{grid-template-columns:110px 1fr}.wbf-size{font-size:22px}}' +
    '@media (prefers-reduced-motion:reduce){.wbf-card{animation:none}.wbf-meter i{transition:none}}';

  function injectCss() {
    if (document.getElementById('wbf-css')) return;
    var st = document.createElement('style'); st.id = 'wbf-css'; st.textContent = css; document.head.appendChild(st);
  }

  function render(ctx) {
    injectCss();
    var tops = ctx.kind === 'tops';
    var gtype = garmentType(ctx.ptype, ctx.kind);
    var color = colorOf(ctx.color);
    var data = build(ctx);
    var sizes = data.sizes;
    if (!sizes.length) { ctx.el.innerHTML = '<p class="sg-err">We could not match this product to a size chart.</p>'; return; }

    var FITS = tops ? FITS_TOP : FITS_BOTTOM;
    var prefKey = ctx.fit < 0 ? 'fitted' : ctx.fit > 0 ? 'relaxed' : 'regular';
    if (ctx.oversized && tops) prefKey = ctx.fit > 0 ? 'oversized' : prefKey;

    /* best size for each fit style (in stock first) */
    var recs = [];
    FITS.forEach(function (f) {
      var best = null;
      sizes.forEach(function (s) {
        if (s.fit.key === 'tight' || s.fit.key === 'big' || s.fit.key === 'short') return;
        var d = Math.abs(s.ease - f.target) + (s.soldOut ? 2.5 : 0);
        if (!best || d < best.d) best = { s: s, d: d };
      });
      if (!best) return;
      /* only keep a style if that size really gives that kind of fit */
      var tol = tops ? 6 : 2.6;
      if (Math.abs(best.s.ease - f.target) > tol) return;
      var existing = recs.filter(function (r) { return r.s === best.s; })[0];
      if (existing) { existing.fits.push(f); return; }
      recs.push({ s: best.s, fits: [f] });
    });
    if (!recs.length) {
      var closest = sizes.slice().sort(function (a, b) { return Math.abs(a.ease - (tops ? 13 : 3)) - Math.abs(b.ease - (tops ? 13 : 3)); })[0];
      recs.push({ s: closest, fits: [{ key: 'regular', name: closest.fit.name, desc: 'Closest size we have' }] });
    }
    var prefRec = recs.filter(function (r) { return r.fits.some(function (f) { return f.key === prefKey; }); })[0] || recs[0];

    /* is the customer's real size buyable? if not, say so and point to the closest ones */
    var prefT = (FITS.filter(function (f) { return f.key === prefKey; })[0] || FITS[1]).target;
    var id = idealOf(ctx, prefT), note = '';
    if (id && (!id.s.made || id.s.soldOut)) {
      var nb = nearby(id.all, id.s);
      note = '<p class="wbf-note">Your size is <b>' + esc(id.s.size) + '</b>, but it ' + whyOut(id.s) + '.';
      if (nb.smaller || nb.larger) note += ' Closest we have: ' + [nb.smaller, nb.larger].filter(Boolean).map(function (s) { return '<b>' + esc(s.size) + '</b> (' + s.feel + ')'; }).join(' or ') + '.';
      else note += ' Try another colour, or message us and we\'ll help.';
      note += '</p>';
    }

    /* their size is in stock → select it straight away (no need to press Select); other sizes stay below */
    var autoPicked = null;
    if (ctx.autoPick && id && id.s.made && !id.s.soldOut && id.s.pick) autoPicked = id.s;
    if (autoPicked) note = '<p class="wbf-picked"><span>✓</span><span>We selected <b>' + esc(autoPicked.size) + '</b> for you. Want a different fit? Choose another size below.</span><button type="button" class="wbf-done">Done</button></p>' + note;

    var you = ctx.h + ' cm · ' + ctx.w + ' kg';
    var html = note +'<div class="wbf-you">You: <b>' + you + '</b> · ' + (tops ? 'chest' : 'waist') + ' ≈ ' + Math.round(data.measure) + ' cm</div>';
    html += '<div class="wbf-recs">';
    recs.forEach(function (r, n) {
      var isPref = r === prefRec;
      var f0 = r.fits[0];
      var title = r.fits.map(function (f) { return f.name; }).join(' / ');
      var room = r.s.ease;
      html += '<div class="wbf-card' + (isPref ? ' is-pref' : '') + '" data-i="' + r.s.i + '" style="animation-delay:' + (n * 0.07) + 's">' +
        (isPref ? '<span class="wbf-tag">Best for you</span>' : '') +
        drawFigure({ h: ctx.h, w: ctx.w, gtype: gtype, ratio: r.s.ratio, color: color, kind: ctx.kind, label: r.s.size + ' ' + title }) +
        '<div class="wbf-fit">' + title + '</div>' +
        '<div class="wbf-size">' + r.s.size + '</div>' +
        '<div class="wbf-desc">' + f0.desc + '</div>' +
        '<div class="wbf-room">' + (room >= 0 ? '+' : '') + Math.round(room) + ' cm room at the ' + (tops ? 'chest' : 'waist') + '</div>' +
        (r.s.pick ? '<button type="button" class="wbf-btn' + (autoPicked && r.s.pick === autoPicked.pick ? ' is-on' : '') + '"' + (r.s.soldOut ? ' disabled>Sold out in this colour' : (autoPicked && r.s.pick === autoPicked.pick ? '>✓ Selected' : '>Select ' + r.s.size)) + '</button>' : '') +
        '</div>';
    });
    html += '</div>';

    /* try every size on */
    html += '<div class="wbf-try"><h4>Try every size on you</h4><p>Tap a size to see how this piece fits your body.</p>' +
      '<div class="wbf-chips">' + sizes.map(function (s, n) { return '<button type="button" class="wbf-chip' + (s.soldOut ? ' is-out' : '') + '" data-n="' + n + '" aria-pressed="false">' + s.size + '</button>'; }).join('') + '</div>' +
      '<div class="wbf-stage"><div class="wbf-fig"></div><div><div class="wbf-meter"><i></i></div><div class="wbf-scale">' +
      (tops ? '<span>Tight</span><span>Fitted</span><span>Regular</span><span>Relaxed</span><span>Oversized</span>' : '<span>Tight</span><span>Snug</span><span>Regular</span><span>Relaxed</span><span>Too big</span>') +
      '</div><div class="wbf-now"></div></div></div></div>';
    html += '<p class="wbf-disc">Your size is a recommendation based on your height and weight and this item\'s measurements – real fabrics and cuts can vary a little.</p>';
    ctx.el.innerHTML = html;

    /* highlight the chart row of the best size */
    ctx.rows.forEach(function (r) { r.el.classList.remove('is-hit'); });
    prefRec.s.row.el.classList.add('is-hit');

    each(ctx.el.querySelectorAll('.wbf-card'), function (card) {
      var s = sizes.filter(function (x) { return x.i === +card.dataset.i; })[0];
      var b = card.querySelector('.wbf-btn');
      if (b && s && s.pick && !s.soldOut) b.addEventListener('click', function (e) { e.stopPropagation(); ctx.select(s.pick); });
      card.addEventListener('click', function () { show(sizes.indexOf(s)); var t = ctx.el.querySelector('.wbf-try'); if (t && t.scrollIntoView) t.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); });
    });

    var fig = ctx.el.querySelector('.wbf-fig'), now = ctx.el.querySelector('.wbf-now'), dot = ctx.el.querySelector('.wbf-meter i');
    function show(n) {
      var s = sizes[n]; if (!s) return;
      each(ctx.el.querySelectorAll('.wbf-chip'), function (c) { c.setAttribute('aria-pressed', +c.dataset.n === n ? 'true' : 'false'); });
      fig.innerHTML = drawFigure({ h: ctx.h, w: ctx.w, gtype: gtype, ratio: s.ratio, color: color, kind: ctx.kind, label: s.size });
      var pos = tops ? (s.ease + 4) / 40 : (s.ease + 4) / 16;
      dot.style.left = (Math.max(0.02, Math.min(0.98, pos)) * 100) + '%';
      var msg = '<b>' + s.size + '</b> → ' + s.fit.name + ' on you';
      if (s.fit.key === 'tight') msg += '. We don\'t recommend this size.';
      else if (s.fit.key === 'big') msg += '. Too big on the waist.';
      else if (s.fit.key === 'short') msg += '. Made for shorter people – it will be short on you.';
      msg += '<br><span style="opacity:.7;font-size:12px">' + (s.ease >= 0 ? '+' : '') + Math.round(s.ease) + ' cm room at the ' + (tops ? 'chest' : 'waist') + (s.soldOut ? ' · sold out in this colour' : '') + '</span>';
      now.innerHTML = msg;
    }
    var each2 = ctx.el.querySelectorAll('.wbf-chip');
    each(each2, function (c) { c.addEventListener('click', function () { show(+c.dataset.n); }); });
    var done = ctx.el.querySelector('.wbf-done'); if (done && ctx.close) done.addEventListener('click', ctx.close);
    if (autoPicked) {
      ctx.autoPick(autoPicked.pick);
      var hit = sizes.filter(function (x) { return x.pick === autoPicked.pick; })[0];
      if (hit) { ctx.rows.forEach(function (r) { r.el.classList.remove('is-hit'); }); hit.row.el.classList.add('is-hit'); }
    }
    show(sizes.indexOf(autoPicked ? (sizes.filter(function (x) { return x.pick === autoPicked.pick; })[0] || prefRec.s) : prefRec.s));
  }

  function each(l, f) { Array.prototype.forEach.call(l, f); }


  /* ---------- hook into the size-guide popup of the theme (sections/wb-product-extras) ---------- */
  var norm = function (v) { return String(v).toUpperCase().replace(/\s+/g, '').replace(/^XXXL$/, '3XL').replace(/^XXL$/, '2XL').replace(/^XXXXL$/, '4XL'); };
  var matchSize = function (list, size) {
    var k = norm(size), i;
    for (i = 0; i < list.length; i++) if (list[i].key === k) return list[i];
    var w = k.replace(/^W/, ''), cands = [];
    for (i = 0; i < list.length; i++) { var m = list[i].key.match(/^W?(\d{2})/); if (m && m[1] === w) cands.push(list[i]); }
    if (cands.length < 2) return cands[0] || null;
    /* jeans with W + L in one option: pick the leg length that suits the customer's height (in stock first) */
    var wantL = curH < 170 ? 30 : curH <= 182 ? 32 : 34;
    cands.sort(function (a, b) {
      var la = +((a.key.match(/L(\d{2})/) || [])[1] || 32), lb = +((b.key.match(/L(\d{2})/) || [])[1] || 32);
      return (Math.abs(la - wantL) + (a.soldOut ? 5 : 0)) - (Math.abs(lb - wantL) + (b.soldOut ? 5 : 0));
    });
    return cands[0];
  };
  var curH = 175;
  var optionFieldset = function (re) {
    var found = null;
    each(document.querySelectorAll('variant-selects fieldset, .product-form__input'), function (fs) {
      var lg = fs.querySelector('legend, .form__label');
      if (!found && lg && re.test(lg.textContent)) found = fs;
    });
    return found;
  };
  var saveProfile = function (h, w, fit) {
    try { localStorage.setItem('wbFit', JSON.stringify({ h: h, w: w, fit: fit || 0 })); } catch (e) {}
    try { window.dispatchEvent(new CustomEvent('wb:size-profile')); } catch (e) {}
  };
  /* the variant picker is re-rendered (new input ids) after every change – always click the live input for that size */
  var clickSize = function (p) {
    var fs = optionFieldset(/size|مقاس/i), input = null;
    if (fs) each(fs.querySelectorAll('input[type="radio"]'), function (i) { if (!input && i.value === p.value) input = i; });
    input = input || p.input;
    if (!input || input.checked) return;
    var lab = (input.labels && input.labels[0]) || (input.id && document.querySelector('label[for="' + input.id + '"]'));
    (lab || input).click();
  };
  function attach() {
    var g = document.querySelector('.wb-sg');
    if (!g || g.dataset.wbf) return;
    var go = g.querySelector('.sg-go'); if (!go) return;
    g.dataset.wbf = '1';
    var fresh = go.cloneNode(true); go.parentNode.replaceChild(fresh, go);   /* drop the simple finder, use this one */
    var res = g.querySelector('.sg-res');
    var close = function () { g.classList.remove('is-open'); document.documentElement.style.overflow = ''; };
    fresh.addEventListener('click', function () {
      var hIn = g.querySelector('input[id^="wb-sg-h"]'), wIn = g.querySelector('input[id^="wb-sg-w"]');
      var h = +hIn.value, w = +wIn.value;
      if (!h || !w || h < 130 || h > 220 || w < 35 || w > 200) { res.innerHTML = '<p class="sg-err">Please enter your height in cm and weight in kg.</p>'; return; }
      var fitBtn0 = g.querySelector('.sg-fit [aria-pressed="true"]');
      saveProfile(h, w, fitBtn0 ? +fitBtn0.getAttribute('data-fit') : 0);
      curH = h;
      setTimeout(autoSize, 50);
      var head = [].map.call(g.querySelectorAll('thead th'), function (th) { return th.textContent.toLowerCase(); });
      var rows = [].map.call(g.querySelectorAll('tbody tr'), function (tr) { return { el: tr, size: tr.getAttribute('data-size'), cells: [].map.call(tr.children, function (td) { return td.textContent; }) }; });
      var sizes = [];
      var sfs = optionFieldset(/size|مقاس/i);
      if (sfs) each(sfs.querySelectorAll('input[type="radio"]'), function (i) { sizes.push({ value: i.value, key: norm(i.value), input: i, soldOut: i.classList.contains('disabled') || i.disabled }); });
      var color = '';
      var cfs = optionFieldset(/colou?r|لون/i);
      if (cfs) { var c = cfs.querySelector('input:checked'); if (c) color = c.value; }
      var fitBtn = g.querySelector('.sg-fit [aria-pressed="true"]');
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      render({
        el: res, rows: rows, head: head, kind: g.getAttribute('data-kind'), oversized: g.getAttribute('data-oversized') === 'true',
        h: h, w: w, fit: fitBtn ? +fitBtn.getAttribute('data-fit') : 0, sizes: sizes, match: matchSize,
        ptype: (meta && meta.type) || g.getAttribute('data-ptype') || '', color: color,
        select: function (p) { clickSize(p); close(); },
        autoPick: function (p) { clickSize(p); },
        close: close
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attach); else attach();

  /* ---------- remember the customer: show "Your size" on every product + in the size chart ---------- */
  var readCtx = function (g, h, w, fit) {
    var head = [].map.call(g.querySelectorAll('thead th'), function (th) { return th.textContent.toLowerCase(); });
    var rows = [].map.call(g.querySelectorAll('tbody tr'), function (tr) { return { el: tr, size: tr.getAttribute('data-size'), cells: [].map.call(tr.children, function (td) { return td.textContent; }) }; });
    var sizes = [];
    var sfs = optionFieldset(/size|مقاس/i);
    if (sfs) each(sfs.querySelectorAll('input[type="radio"]'), function (i) { sizes.push({ value: i.value, key: norm(i.value), input: i, soldOut: i.classList.contains('disabled') || i.disabled }); });
    return { rows: rows, head: head, kind: g.getAttribute('data-kind'), oversized: g.getAttribute('data-oversized') === 'true', h: h, w: w, fit: fit || 0, sizes: sizes, match: matchSize, fs: sfs };
  };
  var pickInput = function (s) { if (s && s.pick) clickSize(s.pick); };
  var openGuide = function (pane) {
    var l = document.querySelector('.wb-sg-link[data-p="' + (pane || 'find') + '"]');
    if (l) { l.click(); return; }
    var g = document.querySelector('.wb-sg'); if (!g) return;
    var t = g.querySelector('.sg-tab[data-pane="' + (pane || 'find') + '"]'); if (t) t.click();
  };

  /* "Your size" box at the top of the Size chart tab */
  function chartBanner(g, info) {
    var pane = g.querySelector('.sg-pane[data-pane="chart"]'); if (!pane) return;
    var box = pane.querySelector('.wbf-chartyou');
    if (!box) { box = document.createElement('div'); box.className = 'wbf-chartyou'; pane.insertBefore(box, pane.firstChild); }
    each(g.querySelectorAll('tbody tr'), function (tr) { tr.classList.remove('is-you'); });
    if (!info) {
      box.innerHTML = '<p>Want to know your size? <button type="button" class="wbf-linkbtn" data-go="find">Enter your height &amp; weight</button> and we\'ll mark it in the chart.</p>';
    } else {
      var s = info.ideal, ok = s.made && !s.soldOut;
      s.row.el.classList.add('is-you', 'is-hit');
      var h = '<div class="wbf-chartyou__top"><span class="wbf-chartyou__badge">' + esc(s.size) + '</span><div><b>Your size: ' + esc(s.size) + '</b>' +
        '<small>Recommended from your height &amp; weight (' + info.p.h + ' cm · ' + info.p.w + ' kg). It\'s a guide, not a guarantee.</small></div>' +
        '<button type="button" class="wbf-linkbtn" data-go="find">Change</button></div>';
      if (!ok) {
        h += '<p class="wbf-chartyou__alt">Size <b>' + esc(s.size) + '</b> ' + whyOut(s) + '.';
        if (info.smaller || info.larger) {
          h += ' Closest we have: ';
          h += [info.smaller, info.larger].filter(Boolean).map(function (x) { return '<button type="button" class="wbf-alt" data-alt="' + x.i + '"><b>' + esc(x.size) + '</b> ' + x.feel + '</button>'; }).join(' ');
        } else h += ' Try another colour, or message us and we\'ll help.';
        h += '</p>';
      }
      box.innerHTML = h;
      each(box.querySelectorAll('[data-alt]'), function (b) {
        var x = [info.smaller, info.larger].filter(function (y) { return y && y.i === +b.dataset.alt; })[0];
        b.addEventListener('click', function () { pickInput(x); g.classList.remove('is-open'); document.documentElement.style.overflow = ''; });
      });
    }
    var go = box.querySelector('[data-go]'); if (go) go.addEventListener('click', function () { var t = g.querySelector('.sg-tab[data-pane="find"]'); if (t) t.click(); });
  }

  var autoDone = false;
  function autoSize() {
    var g = document.querySelector('.wb-sg'); if (!g) return;
    var saved = null; try { saved = JSON.parse(localStorage.getItem('wbFit') || 'null'); } catch (e) {}
    if (!saved || !saved.h || !saved.w) { chartBanner(g, null); return; }
    curH = saved.h;
    var ctx = readCtx(g, saved.h, saved.w, saved.fit);
    var old = ctx.fs && ctx.fs.querySelector('.wbf-mine'); if (old) old.remove();
    var tgt = targetOf(ctx.kind, saved.fit || 0);
    var id = idealOf(ctx, tgt);
    if (!id) return;
    var s = id.s, ok = s.made && !s.soldOut;
    var nb = ok ? { smaller: null, larger: null } : nearby(id.all, s);
    chartBanner(g, { ideal: s, smaller: nb.smaller, larger: nb.larger, p: saved });
    if (!ctx.fs) return;

    var fitWord = saved.fit < 0 ? 'fitted' : saved.fit > 0 ? 'loose' : 'regular';
    var pill = document.createElement('div');
    pill.className = 'wbf-mine' + (ok ? '' : ' is-out');
    var h = '<span class="wbf-mine__ic">' + (ok ? '✓' : '!') + '</span><span>Your size: <b>' + esc(s.size) + '</b> <small>' + fitWord + ' fit</small></span>' +
      (ok ? '' : '<em>' + (s.made ? 'Sold out in this colour' : 'Not made in this item') + '</em>') + '<button type="button" class="wbf-mine__chg">Change</button>';
    if (!ok) {
      if (nb.smaller || nb.larger) {
        h += '<div class="wbf-mine__alts"><span>Closest we have:</span>' + [nb.smaller, nb.larger].filter(Boolean).map(function (x) {
          return '<button type="button" data-alt="' + x.i + '"><b>' + esc(x.size) + '</b> ' + x.feel + '</button>';
        }).join('') + '</div>';
      } else h += '<div class="wbf-mine__alts"><span>No close size in this colour – try another colour.</span></div>';
    }
    h += '<p class="wbf-mine__why">Based on your height &amp; weight (' + saved.h + ' cm · ' + saved.w + ' kg) – a recommendation, not a guarantee.</p>';
    pill.innerHTML = h;
    pill.querySelector('.wbf-mine__chg').addEventListener('click', function () { openGuide('find'); });
    each(pill.querySelectorAll('[data-alt]'), function (b) {
      var x = [nb.smaller, nb.larger].filter(function (y) { return y && y.i === +b.dataset.alt; })[0];
      b.addEventListener('click', function () { pickInput(x); });
      if (x && x.pick && x.pick.input.checked) b.setAttribute('aria-pressed', 'true');
    });
    var links = ctx.fs.querySelector('.wb-sg-links'), lg = ctx.fs.querySelector('legend, .form__label');
    (links || lg).insertAdjacentElement('afterend', pill);
    /* they switched colour from "your size is here" → select their size in the new colour */
    if (pendingPick && ok && s.pick) { pendingPick = false; autoDone = true; pickInput(s); }
    /* pick their size for them once – only if it is really their size and in stock */
    else if (!autoDone && ok && s.pick && !s.pick.input.checked && !/[?&]variant=/.test(location.search)) {
      autoDone = true;
      pickInput(s);
    } else autoDone = true;
    if (s.made) markColours(s, ctx.kind, pill, ok);
  }

  /* ---------- "Your size is here": show on the colour swatches which colours have their size in stock ---------- */
  var pendingPick = false, productJson = null;
  var loadProduct = function () {
    if (productJson) return productJson;
    var path = location.pathname.replace(/\/$/, '');
    productJson = fetch(path + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).catch(function () { productJson = null; return null; });
    return productJson;
  };
  var sameSize = function (kind, chartSize, value) {
    var a = norm(chartSize), b = norm(value);
    if (a === b) return true;
    if (kind === 'jeans' || kind === 'bottoms') {
      var wa = a.match(/^W?(\d{2})/), wb = b.match(/^W?(\d{2})/);
      return !!(wa && wb && wa[1] === wb[1] && /^W?\d{2}(\/?L\d{2})?$/.test(b));
    }
    return false;
  };
  function markColours(s, kind, pill, okHere) {
    var cfs = optionFieldset(/colou?r|لون/i); if (!cfs) return;
    var radios = cfs.querySelectorAll('input[type="radio"]');
    if (radios.length < 2) return;
    loadProduct().then(function (p) {
      if (!p || !p.options) return;
      var names = p.options.map(function (o) { return String(o.name || o).toLowerCase(); });
      var ci = -1, si = -1;
      names.forEach(function (n, i) { if (ci < 0 && /colou?r|لون/.test(n)) ci = i; if (si < 0 && /size|مقاس/.test(n)) si = i; });
      if (ci < 0 || si < 0) return;
      var has = {};
      p.variants.forEach(function (v) { if (v.available && sameSize(kind, s.row.size, v.options[si])) has[v.options[ci]] = true; });
      var live = optionFieldset(/colou?r|لون/i); if (!live) return;
      var list = [], current = '';
      each(live.querySelectorAll('input[type="radio"]'), function (inp) {
        var lab = (inp.labels && inp.labels[0]) || live.querySelector('label[for="' + inp.id + '"]');
        if (!lab) return;
        var old = lab.querySelector('.wbf-sz'); if (old) old.remove();
        lab.classList.remove('wbf-has', 'wbf-hasnt');
        if (inp.checked) current = inp.value;
        if (has[inp.value]) {
          lab.classList.add('wbf-has');
          var dot = document.createElement('span'); dot.className = 'wbf-sz'; dot.setAttribute('aria-hidden', 'true'); dot.textContent = '✓';
          lab.appendChild(dot);
          lab.setAttribute('title', inp.value + ' – your size ' + s.row.size + ' in stock');
          list.push({ v: inp.value, lab: lab, on: inp.checked });
        } else lab.classList.add('wbf-hasnt');
      });
      var line = live.querySelector('.wbf-colorsize');
      if (!line) { line = document.createElement('div'); line.className = 'wbf-colorsize'; var lg = live.querySelector('legend, .form__label'); if (lg) lg.insertAdjacentElement('afterend', line); else live.insertBefore(line, live.firstChild); }
      if (!list.length) { line.innerHTML = '<span>Your size <b>' + esc(s.row.size) + '</b> is sold out in every colour right now.</span>'; return; }
      line.innerHTML = '<span><i>✓</i> Your size <b>' + esc(s.row.size) + '</b> is here:</span>' + list.map(function (x, n) {
        return '<button type="button" data-n="' + n + '"' + (x.on ? ' aria-pressed="true"' : '') + '>' + esc(x.v) + '</button>';
      }).join('');
      var go = function (x) { if (x.on) return; pendingPick = true; x.lab.click(); };
      each(line.querySelectorAll('button'), function (b) { b.addEventListener('click', function () { go(list[+b.dataset.n]); }); });
      /* their size is sold out in this colour → point straight to the colours that have it */
      if (!okHere && pill && pill.isConnected && !pill.querySelector('.wbf-mine__here')) {
        var here = document.createElement('div'); here.className = 'wbf-mine__alts wbf-mine__here';
        here.innerHTML = '<span>Your size ' + esc(s.row.size) + ' in other colours:</span>' + list.map(function (x, n) { return '<button type="button" data-n="' + n + '"><b>' + esc(x.v) + '</b></button>'; }).join('');
        each(here.querySelectorAll('button'), function (b) { b.addEventListener('click', function () { go(list[+b.dataset.n]); }); });
        var why = pill.querySelector('.wbf-mine__why'); pill.insertBefore(here, why);
        var none = pill.querySelector('.wbf-mine__alts:not(.wbf-mine__here) span'); if (none && /No close size/.test(none.textContent)) none.parentNode.remove();
      }
    });
  }
  var mineCss = '.wbf-mine{display:flex;align-items:center;flex-wrap:wrap;gap:8px;margin:0 0 12px;padding:8px 12px;border-radius:12px;border:1px solid var(--wb-acc,#E3FF4A);background:rgba(227,255,74,.08);font-size:13px;animation:wbfIn .4s cubic-bezier(.2,.8,.2,1)}' +
    '.wbf-mine.is-out{border-color:#ffb84d;background:rgba(255,184,77,.07)}' +
    '.wbf-mine__ic{width:20px;height:20px;border-radius:50%;display:grid;place-items:center;background:var(--wb-acc,#E3FF4A);color:#111;font-weight:900;font-size:12px}' +
    '.wbf-mine.is-out .wbf-mine__ic{background:#ffb84d}' +
    '.wbf-mine b{font-size:15px}.wbf-mine small{opacity:.7;margin-left:4px}.wbf-mine em{font-style:normal;color:#ffb84d;font-size:12px}' +
    '.wbf-mine__chg{margin-left:auto;background:none;border:0;color:inherit;font:inherit;font-size:12px;font-weight:700;text-decoration:underline;text-underline-offset:3px;cursor:pointer}' +
    '.wbf-mine__alts{flex-basis:100%;display:flex;flex-wrap:wrap;align-items:center;gap:6px;font-size:12px}' +
    '.wbf-mine__alts span{opacity:.8}' +
    '.wbf-mine__alts button,.wbf-alt{display:inline-flex;align-items:center;gap:5px;height:32px;padding:0 12px;border-radius:999px;border:1px solid rgba(var(--color-foreground),.3);background:none;color:inherit;font:inherit;font-size:12px;cursor:pointer}' +
    '.wbf-mine__alts button b,.wbf-alt b{font-size:14px}' +
    '.wbf-mine__alts button:hover,.wbf-alt:hover,.wbf-mine__alts button[aria-pressed="true"]{border-color:var(--wb-acc,#E3FF4A)}' +
    '.wbf-mine__why{flex-basis:100%;margin:0;font-size:11px;opacity:.6;line-height:1.4}' +
    '.wbf-chartyou{margin:0 0 14px;padding:12px;border-radius:12px;border:1px solid var(--wb-acc,#E3FF4A);background:rgba(227,255,74,.07);font-size:13px;line-height:1.45}' +
    '.wbf-chartyou p{margin:0}' +
    '.wbf-chartyou__top{display:flex;align-items:center;gap:12px}' +
    '.wbf-chartyou__top b{display:block;font-size:15px}.wbf-chartyou__top small{display:block;opacity:.7;font-size:12px}' +
    '.wbf-chartyou__top .wbf-linkbtn{margin-left:auto}' +
    '.wbf-chartyou__badge{flex:0 0 auto;min-width:48px;height:48px;padding:0 8px;border-radius:12px;display:grid;place-items:center;background:var(--wb-acc,#E3FF4A);color:#111;font-weight:900;font-size:16px}' +
    '.wbf-chartyou__alt{margin-top:10px !important;padding-top:10px;border-top:1px dashed rgba(var(--color-foreground),.2)}' +
    '.wbf-chartyou__alt .wbf-alt{margin:4px 4px 0 0}' +
    '.wbf-linkbtn{padding:0;background:none;border:0;color:inherit;font:inherit;font-size:12px;font-weight:700;text-decoration:underline;text-underline-offset:3px;cursor:pointer}' +
    '.wbf-has{position:relative}' +
    '.wbf-sz{position:absolute;top:-5px;right:-5px;width:17px;height:17px;border-radius:50%;display:grid;place-items:center;background:var(--wb-acc,#E3FF4A);color:#111;font-size:10px;font-weight:900;line-height:1;border:2px solid rgb(var(--color-background));pointer-events:none;z-index:2}' +
    '.wbf-hasnt .swatch{opacity:.45}' +
    '.wbf-colorsize{display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin:2px 0 10px;font-size:12px}' +
    '.wbf-colorsize>span{opacity:.85}.wbf-colorsize i{font-style:normal;display:inline-grid;place-items:center;width:16px;height:16px;margin-right:4px;border-radius:50%;background:var(--wb-acc,#E3FF4A);color:#111;font-size:10px;font-weight:900}' +
    '.wbf-colorsize button{height:28px;padding:0 10px;border-radius:999px;border:1px solid rgba(var(--color-foreground),.3);background:none;color:inherit;font:inherit;font-size:12px;cursor:pointer}' +
    '.wbf-colorsize button:hover{border-color:var(--wb-acc,#E3FF4A)}' +
    '.wbf-colorsize button[aria-pressed="true"]{background:var(--wb-acc,#E3FF4A);border-color:var(--wb-acc,#E3FF4A);color:#111;cursor:default}' +
    '.wb-sg tr.is-you td:first-child{position:relative}' +
    '.wb-sg tr.is-you td:first-child::after{content:"YOU";position:absolute;top:2px;left:2px;padding:0 4px;border-radius:4px;background:#111;color:var(--wb-acc,#E3FF4A);font-size:8px;font-weight:900;letter-spacing:.06em;line-height:1.5}';
  function startAuto() {
    injectCss();
    if (!document.getElementById('wbf-mine-css')) { var st = document.createElement('style'); st.id = 'wbf-mine-css'; st.textContent = mineCss; document.head.appendChild(st); }
    autoSize();
    /* the variant picker is re-rendered on colour change – put the pill back and refresh stock */
    var pi = document.querySelector('product-info') || document.querySelector('main');
    var t = null;
    if (pi) new MutationObserver(function () {
      var fs = optionFieldset(/size|مقاس/i);
      if (fs && !fs.querySelector('.wbf-mine')) { clearTimeout(t); t = setTimeout(autoSize, 120); }
    }).observe(pi, { childList: true, subtree: true });
    window.addEventListener('storage', function (e) { if (e.key === 'wbFit') autoSize(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(startAuto, 300); }); else setTimeout(startAuto, 300);

  window.WBFit = { render: render, drawFigure: drawFigure, colorOf: colorOf, garmentType: garmentType };
})();
